import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Users, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { fmtDate, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, MiniEmpty } from './DashboardShared';

const COMPANY_COLS = [
  {
    key: 'name',
    label: 'Entreprise',
    render: (v, row) => (
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-primary/10 grid place-items-center shrink-0">
          <Building2 size={12} strokeWidth={1.75} className="text-primary" />
        </div>
        <div>
          <p className="font-medium text-slate-800 text-[12.5px]">{v}</p>
          {row.erpCode && (
            <p className="text-[10.5px] text-slate-400 font-mono">{row.erpCode}</p>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'ice',
    label: 'ICE',
    render: (v) => <span className="font-mono text-[12px] text-slate-500">{v || '—'}</span>,
  },
  {
    key: 'contactInfo',
    label: 'Contact',
    render: (v) => <span className="text-slate-500 text-[12px]">{v?.email || v?.phone || '—'}</span>,
  },
  {
    key: 'status',
    label: 'Statut',
    render: (v) => (
      <Badge variant={v === 'ACTIVE' ? 'success' : 'preparing'}>
        {v === 'ACTIVE' ? 'Actif' : 'Inactif'}
      </Badge>
    ),
  },
  {
    key: 'createdAt',
    label: 'Créé le',
    render: (v) => <span className="text-slate-500 text-[12px]">{fmtDate(v)}</span>,
  },
];

const USER_COLS = [
  {
    key: 'name',
    label: 'Utilisateur',
    render: (_, row) => (
      <div>
        <p className="font-medium text-slate-800 text-[12.5px]">{row.firstName} {row.lastName}</p>
        <p className="text-[11px] text-slate-400 font-mono">{row.username}</p>
      </div>
    ),
  },
  {
    key: 'email',
    label: 'Email',
    render: (v) => <span className="text-slate-500 text-[12px]">{v || '—'}</span>,
  },
  {
    key: 'role',
    label: 'Rôle',
    render: (v) => {
      const ROLE_VARIANT = {
        admin: 'info', direction: 'transit', finance: 'paid', operations: 'preparing', achats: 'warning',
      };
      const roleName = v?.name ?? v;
      return roleName ? <Badge variant={ROLE_VARIANT[roleName] || 'info'}>{roleName}</Badge> : <span className="text-slate-400">—</span>;
    },
  },
  {
    key: 'isActive',
    label: 'Statut',
    render: (v) => <Badge variant={v ? 'success' : 'preparing'}>{v ? 'Actif' : 'Inactif'}</Badge>,
  },
  {
    key: 'lastLoginAt',
    label: 'Dernière connexion',
    render: (v) => <span className="text-slate-500 text-[12px]">{fmtDate(v)}</span>,
  },
];

export default function GlobalAdminDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([
      api.post('/companies/get-all?skip=0&limit=500', { type: 'CLIENT' }),
      api.get('/users'),
    ])
      .then(([cRes, uRes]) => {
        const raw = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data ?? []);
        setCompanies(raw.filter((c) => c.type === 'CLIENT'));
        setUsers(Array.isArray(uRes.data?.data) ? uRes.data.data : []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => ({
    total:    companies.length,
    active:   companies.filter((c) => c.status === 'ACTIVE').length,
    inactive: companies.filter((c) => c.status === 'INACTIVE').length,
    users:    users.length,
    activeUsers: users.filter((u) => u.isActive).length,
  }), [companies, users]);

  const monthlyData    = useMemo(() => groupByMonth(companies, 'createdAt'), [companies]);
  const recentCompanies = useMemo(() =>
    [...companies].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5),
    [companies]
  );
  const recentUsers    = useMemo(() =>
    [...users].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5),
    [users]
  );

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Administration globale"
        sub={`Bonjour ${user?.firstName} — vue plateforme`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Clients enregistrés"
          value={loading ? '—' : kpis.total}
          icon={Building2}
          sparkline={!loading && companies.length ? monthlyData.map((d) => d.value) : undefined}
        />
        <KpiCard
          label="Clients actifs"
          value={loading ? '—' : kpis.active}
          sub={!loading && kpis.total ? `${Math.round((kpis.active / kpis.total) * 100)} % du total` : undefined}
          trend={!loading && kpis.active > 0 ? 'up' : undefined}
          trendLabel={!loading && kpis.active > 0 ? `${kpis.active} actifs` : undefined}
        />
        <KpiCard
          label="Utilisateurs totaux"
          value={loading ? '—' : kpis.users}
          icon={Users}
          sub={!loading ? `${kpis.activeUsers} actifs` : undefined}
        />
        <KpiCard
          label="Clients inactifs"
          value={loading ? '—' : kpis.inactive}
          icon={UserCheck}
          trendLabel={!loading && kpis.inactive === 0 ? 'Tous actifs' : undefined}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Nouveaux clients par mois" className="col-span-2">
          {!loading && <BarChart data={monthlyData} />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Aperçu plateforme">
          {loading
            ? <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-5 bg-slate-100 rounded animate-pulse" />)}</div>
            : (
              <div className="space-y-3">
                {[
                  { label: 'Clients actifs',   value: kpis.active,      total: kpis.total, color: '#059669' },
                  { label: 'Clients inactifs', value: kpis.inactive,    total: kpis.total, color: '#94a3b8' },
                  { label: 'Utilisateurs actifs', value: kpis.activeUsers, total: kpis.users, color: '#0369a1' },
                ].map((row) => {
                  const pct = row.total > 0 ? Math.round((row.value / row.total) * 100) : 0;
                  return (
                    <div key={row.label}>
                      <div className="flex justify-between text-[11.5px] mb-1">
                        <span className="text-slate-600">{row.label}</span>
                        <span className="font-semibold text-slate-800">{row.value}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: row.color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Clients récents"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {recentCompanies.length}
            </span>
          }
        >
          {recentCompanies.length > 0
            ? <Table columns={[...COMPANY_COLS, ...timestampColumns]} rows={recentCompanies} rowKey="_id" />
            : <MiniEmpty label="Aucun client enregistré" />
          }
        </SectionCard>

        <SectionCard
          title="Utilisateurs récents"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {recentUsers.length}
            </span>
          }
        >
          {recentUsers.length > 0
            ? <Table columns={USER_COLS} rows={recentUsers} rowKey="_id" />
            : <MiniEmpty label="Aucun utilisateur enregistré" />
          }
        </SectionCard>
      </div>
    </div>
  );
}
